/**
 * BedtimeCTABanner
 * CCH Care — Phase 4b
 *
 * Dashboard CTA for the bedtime checklist workflow. Self-suppresses when the
 * facility has zero residents in scope. Color-codes by completion state:
 *   none done → primary blue (call-to-action)
 *   in progress → warning amber
 *   all done → accent green
 *
 * Click navigates to /dashboard/bedtime (BedtimeChecklistPage resident list).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../../config/constants';
import client from '../../../api/client';

const BASE = '/adl/bedtime.php';

export default function BedtimeCTABanner() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    client.get(`${BASE}?action=residents`)
      .then(res => {
        if (cancelled) return;
        if (res.data.success) {
          const residents = res.data.residents || [];
          const total = residents.length;
          const done = residents.filter(r => r.checklist_status === 'completed').length;
          setStats({ total, done });
        }
      })
      .catch(() => {})  // silent fail — banner just doesn't render
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading || !stats || stats.total === 0) return null;

  const { total, done } = stats;
  const allDone     = done === total;
  const noneStarted = done === 0;

  const color  = allDone ? COLORS.accent       : noneStarted ? COLORS.primary       : COLORS.warning;
  const bg     = allDone ? COLORS.accentLight  : noneStarted ? COLORS.primaryLight  : COLORS.warningLight;
  const border = allDone ? COLORS.accentAlpha  : noneStarted ? COLORS.primaryAlpha  : COLORS.warningAlpha;
  const icon   = allDone ? '✅' : '🌙';

  const title    = allDone ? 'All bedtime checklists complete' : 'Bedtime Checklists';
  const subtitle = allDone
    ? `${total} of ${total} done`
    : `${done} of ${total} done — tap to continue`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate('/dashboard/bedtime')}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/dashboard/bedtime');
        }
      }}
      style={{
        margin: '12px 16px 4px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 10,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>
          {subtitle}
        </div>
      </div>
      <span style={{ fontSize: 16, color: COLORS.textMuted }}>›</span>
    </div>
  );
}